import { useMemo, useState } from 'react';

const INITIAL_FORM_DATA = {
  serviceType: '',
  customServiceName: '',
  description: '',
  preferredDate: '',
  preferredTime: '',
  duration: '',
  location: '',
  participants: '',
  budget: '',
  specialRequirements: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  additionalNotes: ''
};

const SERVICE_TYPES = [
  { id: 'wedding', name: 'Wedding Ceremony' },
  { id: 'home-pooja', name: 'Home Pooja' },
  { id: 'festival', name: 'Festival Celebration' },
  { id: 'life-event', name: 'Life Event' },
  { id: 'spiritual', name: 'Spiritual Service' },
  { id: 'special-occasion', name: 'Special Occasion' },
  { id: 'other', name: 'Other (Please specify)' }
];

const TIME_SLOTS = [
  '6:00 AM - 8:00 AM',
  '8:00 AM - 10:00 AM',
  '10:00 AM - 12:00 PM',
  '12:00 PM - 2:00 PM',
  '2:00 PM - 4:00 PM',
  '4:00 PM - 6:00 PM',
  '6:00 PM - 8:00 PM',
  '8:00 PM - 10:00 PM'
];

const DURATION_OPTIONS = [
  '1-2 hours',
  '2-3 hours',
  '3-4 hours',
  '4-6 hours',
  'Full day',
  'Multiple days'
];

const PARTICIPANT_OPTIONS = [
  '1-10',
  '11-25',
  '26-50',
  '51-100',
  '100+'
];

const BUDGET_OPTIONS = [
  'Under ₹5,000',
  '₹5,000 - ₹10,000',
  '₹10,000 - ₹25,000',
  '₹25,000 - ₹50,000',
  '₹50,000 - ₹1,00,000',
  'Above ₹1,00,000'
];

const useCustomServiceRequestForm = ({ onNavigate }) => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.serviceType) newErrors.serviceType = 'Service type is required';
    if (!formData.customServiceName) newErrors.customServiceName = 'Service name is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.preferredDate) newErrors.preferredDate = 'Preferred date is required';
    if (!formData.preferredTime) newErrors.preferredTime = 'Preferred time is required';
    if (!formData.location) newErrors.location = 'Location is required';
    if (!formData.contactName) newErrors.contactName = 'Contact name is required';
    if (!formData.contactPhone) newErrors.contactPhone = 'Contact phone is required';
    if (!formData.contactEmail) newErrors.contactEmail = 'Contact email is required';

    if (formData.contactEmail && !/\S+@\S+\.\S+/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }

    if (formData.contactPhone && !/^\d{10}$/.test(formData.contactPhone.replace(/\D/g, ''))) {
      newErrors.contactPhone = 'Please enter a valid 10-digit phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      console.log('Custom service request submitted:', formData);
      alert('Your custom service request has been submitted successfully! Our team will contact you within 24 hours.');

      if (onNavigate) {
        onNavigate('home');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const minDate = useMemo(() => new Date().toISOString().split('T')[0], []);

  return {
    formData,
    errors,
    isSubmitting,
    serviceTypes: SERVICE_TYPES,
    timeSlots: TIME_SLOTS,
    durationOptions: DURATION_OPTIONS,
    participantOptions: PARTICIPANT_OPTIONS,
    budgetOptions: BUDGET_OPTIONS,
    minDate,
    handleInputChange,
    handleSubmit
  };
};

export default useCustomServiceRequestForm;
